import { DateTime } from 'luxon';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { fetchUserLogs, LogsMode } from '@/features/logsMode';
import { WeeklyWorkLog } from '@/types';
import { YandexUser } from '@/types/yandex';

export class AttendeeLogCheckerModel {
  selectedUserId: string | null = null;
  showAllLogs = false;
  isLoading = false;
  error: string | null = null;

  private _taskKey: string | null = null;
  private _date: string | null = null;
  private _availableUsers: YandexUser[] = [];
  private _userLogsCache = new Map<string, WeeklyWorkLog[]>();
  private _abortController: AbortController | null = null;
  private readonly _mode: LogsMode;

  constructor(mode: LogsMode = LogsMode.legacy) {
    this._mode = mode;

    makeObservable<AttendeeLogCheckerModel, '_availableUsers' | '_taskKey' | '_userLogsCache'>(
      this,
      {
        selectedUserId: observable,
        showAllLogs: observable,
        isLoading: observable,
        error: observable,
        _availableUsers: observable,
        _taskKey: observable,
        _userLogsCache: observable,

        filteredLogs: computed,
        availableUsers: computed,
        hasTaskFilter: computed,

        setSelectedUser: action.bound,
        setTaskKey: action.bound,
        toggleShowAll: action.bound,
        setAvailableUsers: action.bound,
        reset: action.bound,
        loadUserLogs: action.bound,
        retry: action.bound,
      },
    );
  }

  get availableUsers(): YandexUser[] {
    return this._availableUsers;
  }

  get hasTaskFilter(): boolean {
    return this._taskKey !== null && this._taskKey !== '';
  }

  get filteredLogs(): WeeklyWorkLog[] {
    if (!this.selectedUserId) return [];

    const logs = this._userLogsCache.get(this.selectedUserId);

    if (!logs) return [];

    if (this.showAllLogs) {
      return [...logs].sort((a, b) => a.taskKey.localeCompare(b.taskKey));
    }

    if (!this._taskKey) return [];

    return logs.filter((log) => log.taskKey === this._taskKey);
  }

  setSelectedUser(uid: string): void {
    this.selectedUserId = uid;

    if (this._date && !this._userLogsCache.has(uid)) {
      this.loadUserLogs(uid, this._date);
    }
  }

  setTaskKey(taskKey: string | null): void {
    this._taskKey = taskKey;
  }

  toggleShowAll(): void {
    this.showAllLogs = !this.showAllLogs;
  }

  setAvailableUsers(users: YandexUser[]): void {
    this._availableUsers = users;

    if (this.selectedUserId) {
      const stillExists = users.some((u) => u.uid === this.selectedUserId);

      if (!stillExists && users.length > 0) {
        this.setSelectedUser(users[0].uid);
      }
    } else if (users.length > 0) {
      this.setSelectedUser(users[0].uid);
    }
  }

  async loadUserLogs(uid: string, date: string): Promise<void> {
    this._date = date;
    this._abortController?.abort();
    this._abortController = new AbortController();

    const dt = DateTime.fromISO(date);
    const from = dt.startOf('day').toISO()!;
    const to = dt.endOf('day').toISO()!;

    this.isLoading = true;
    this.error = null;

    try {
      const logs = await fetchUserLogs({
        mode: this._mode,
        userId: uid,
        from,
        to,
        signal: this._abortController.signal,
      });

      runInAction(() => {
        this._userLogsCache.set(uid, logs);
        this.isLoading = false;
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;

      runInAction(() => {
        this.error = 'Не удалось загрузить логи';
        this.isLoading = false;
      });
    }
  }

  retry(): void {
    if (this.selectedUserId && this._date) {
      this._userLogsCache.delete(this.selectedUserId);
      this.loadUserLogs(this.selectedUserId, this._date);
    }
  }

  initWithDate(date: string): void {
    this._date = date;

    if (this.selectedUserId && !this._userLogsCache.has(this.selectedUserId)) {
      this.loadUserLogs(this.selectedUserId, date);
    }
  }

  reset(): void {
    this._abortController?.abort();
    this._abortController = null;
    this.selectedUserId = null;
    this.showAllLogs = false;
    this.isLoading = false;
    this.error = null;
    this._taskKey = null;
    this._date = null;
    this._availableUsers = [];
    this._userLogsCache.clear();
  }
}
